import { FormInstance } from 'antd';
import { AuthUser } from 'api/AuthApi';
import { Message, MessageEventTypeEnum, MessageTypeEnum } from 'api/MessagesApi';
import { User, UserAccessLevelPeriodEnum, UserAccessLevelPlanEnum, UserAccessLevelStatusEnum } from 'api/UsersApi';
import dayjs from 'dayjs';
import { detect } from 'detect-browser';
import { securityLogger } from 'utils/securityLogger';

const getLogMetadata = (msg: unknown): Record<string, string | number> | undefined => {
  if (msg instanceof Error) {
    return {
      name: msg.name
    };
  }

  if (!msg || typeof msg !== 'object') {
    return undefined;
  }

  const msgRecord = msg as Record<string, unknown>;
  const metadata: Record<string, string | number> = {};

  const status = msgRecord.status || msgRecord.originalStatus;
  if (typeof status === 'number' || typeof status === 'string') {
    metadata.status = status;
  }

  const code = msgRecord.code;
  if (typeof code === 'number' || typeof code === 'string') {
    metadata.code = code;
  }

  return Object.keys(metadata).length ? metadata : undefined;
};

export const logging = (msg) => {
  const metadata = getLogMetadata(msg);

  if (metadata) {
    securityLogger.info('Log entry', metadata);
    return;
  }

  securityLogger.info('Log entry');
};

export const getReferrer = (location: { pathname: string; search: string }): string =>
  `${window.location.origin}${location.pathname}${location.search}`;

export const generateRange = (start: number, stop: number, step = 1): number[] =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

export const getMonthNames = (): string[] => {
  const months: string[] = [];
  const date = new Date();

  for (let i = 0; i < 12; i++) {
    date.setMonth(i);
    months.push(date.toLocaleString('en-US', { month: 'long' }));
  }
  return months;
};

export const getFullName = (user?: User | null, defaultText = 'Unknown') => {
  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
  return fullName || defaultText;
};

export const getInitials = (firstName?: string, lastName?: string) => {
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
};

export const getFullNameOrEmail = (user?: User | null, defaultText = 'Unknown') => {
  return getFullName(user, '') || user?.email || defaultText;
};

export const getAdminAccessDisplayName = (
  adminAccess: { beneficiaryDisplayName?: string | null; displayName?: string | null; user?: User | null },
  defaultText = 'Unknown'
) => adminAccess.beneficiaryDisplayName || adminAccess.displayName || getFullNameOrEmail(adminAccess.user, defaultText);

export const getFullNameWithEmail = (user?: User | null, defaultText = 'Unknown') => {
  const fullName = getFullName(user, '');
  return user?.email ? (fullName ? `${fullName} (${user.email})` : user.email) : defaultText;
};

export const formatPrice = (price: number) => {
  return `${Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price)}`;
};

export const formatTime = (time: number) => {
  const seconds = time % 60;
  const minutes = Math.floor(time / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

function nth(n: number): string {
  return ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th';
}

export const getFileExtension = (fileName: string): string => fileName.split('.').pop() || '';

export const isMobileOrTabletBrowser = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = window.navigator.userAgent || '';
  const isIpadLike = /iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && window.navigator.maxTouchPoints > 1);

  return isIpadLike || /iPhone|iPod|Android|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
};

export const getMimeTypeFromExtension = (extension: string, mediaKind?: 'video' | 'audio'): string => {
  switch (extension.toLowerCase()) {
    case 'mp4':
      return mediaKind === 'audio' ? 'audio/mp4' : 'video/mp4';
    case 'webm':
      return mediaKind === 'audio' ? 'audio/webm' : 'video/webm';
    case 'ogv':
      return 'video/ogg';
    case 'mov':
      return 'video/quicktime';
    case 'mp3':
      return 'audio/mpeg';
    case 'wav':
      return 'audio/wav';
    case 'm4a':
      return 'audio/mp4';
    case 'ogg':
      return 'audio/ogg';
    default:
      return '';
  }
};

export const displayDeliveryDate = (message?: Message, defaultText = '-') => {
  if (message?.eventType === MessageEventTypeEnum.Final) {
    return 'At passing';
  }

  if (message?.eventType === MessageEventTypeEnum.Future) {
    return message.eventHint || 'Future Event';
  }

  let deliveryDate = defaultText;
  if (message?.deliveryDay && message?.deliveryMonth) {
    deliveryDate = `${getMonthNames()[message.deliveryMonth - 1]} ${message.deliveryDay}${nth(message.deliveryDay)}`;
    if (message?.deliveryYear) {
      deliveryDate = `${deliveryDate}, ${message.deliveryYear}`;
    }
  }
  return deliveryDate;
};

export const isVideoFile = (fileName: string) => ['mp4', 'mov', 'webm', 'ogv'].includes(getFileExtension(fileName));

export const isAudioFile = (fileName: string) =>
  ['mp3', 'wav', 'webm', 'm4a', 'ogg'].includes(getFileExtension(fileName));

export const canBrowserPlayMediaType = (mimeType: string, mediaKind: 'audio' | 'video') => {
  if (!mimeType || typeof document === 'undefined') {
    return true;
  }

  const mediaElement = document.createElement(mediaKind);

  if (typeof mediaElement.canPlayType !== 'function') {
    return true;
  }

  return mediaElement.canPlayType(mimeType) !== '';
};

export const bytesToSize = (bytes: number) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return 'n/a';
  const i = parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))), 10);
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(0)} ${sizes[i]}`;
};

export const validateForm = (form: FormInstance, response: any, formPrefix = '') => {
  if (response?.data?.status !== 422) {
    return;
  }

  const errors: { [key: string]: { name: string; errors: string[] } } = {};
  response?.data?.errors.forEach((error: { path: string; msg: string }) => {
    const path = formPrefix ? `${formPrefix}${error.path.charAt(0).toUpperCase() + error.path.slice(1)}` : error.path;

    if (!errors[path]) {
      errors[path] = {
        name: path,
        errors: [error.msg]
      };
    }
  });

  const formErrors = Object.values(errors);
  if (formErrors.length) {
    form.setFields(formErrors);
  }
};

export const getResponseErrors = (response: any) => {
  if (response?.data?.status !== 422) {
    return [];
  }

  const errors: string[] = [];
  response?.data?.errors.forEach((error: { path: string; msg: string }) => {
    errors.push(error.msg);
  });

  return errors;
};

export const getError = (response: any) => {
  const responseErrors = getResponseErrors(response);
  if (responseErrors.length) {
    return responseErrors;
  }

  if (response.data?.message) {
    return response.data.message;
  }

  return JSON.stringify(response.data);
};

export const getTimeRemaining = (endTime: Date, shiftTime: number) => {
  const total = +endTime - +new Date() + shiftTime;
  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);

  return {
    total,
    hours,
    minutes,
    seconds
  };
};

export const checkIfDateValid = (day: number, month: number, year?: number | null) => {
  const leapYear = 2024;
  const fixedYear = year || leapYear;
  const date = dayjs(`${fixedYear}-${month}-${day}`, 'YYYY-M-D', true);
  return date.isValid();
};

export const getCurrentUrl = () => {
  return window.location.pathname + window.location.search;
};

export const getAccessLevelPlanPeriodName = (accessLevelPeriod: UserAccessLevelPeriodEnum): string => {
  if (accessLevelPeriod === UserAccessLevelPeriodEnum.Yearly) return 'Annual';
  if (accessLevelPeriod === UserAccessLevelPeriodEnum.Monthly) return 'Monthly';
  if (accessLevelPeriod === UserAccessLevelPeriodEnum.OneTime) return 'Forever';
  return '';
};

export const getAccessLevelPlanName = (accessLevel: UserAccessLevelPlanEnum): string => {
  if (accessLevel === UserAccessLevelPlanEnum.Premium) return 'Premium';
  if (accessLevel === UserAccessLevelPlanEnum.Unlimited) return 'Unlimited';
  return 'Starter';
};

export const getAccessLevelByMessageType = (type: MessageTypeEnum): UserAccessLevelPlanEnum => {
  if (type === MessageTypeEnum.Audio) return UserAccessLevelPlanEnum.Premium;
  if (type === MessageTypeEnum.Video) return UserAccessLevelPlanEnum.Unlimited;
  return UserAccessLevelPlanEnum.Free;
};

export const userHasAccess = (user: AuthUser | null, requiredAccessLevel: UserAccessLevelPlanEnum) => {
  const accessOrder = [
    UserAccessLevelPlanEnum.Free,
    UserAccessLevelPlanEnum.Premium,
    UserAccessLevelPlanEnum.Unlimited
  ];
  return (
    user &&
    user.accessLevelStatus !== UserAccessLevelStatusEnum.Canceled &&
    accessOrder.indexOf(getDisplayAccessLevelPlan(user)) >= accessOrder.indexOf(requiredAccessLevel)
  );
};

export const hasOrganizationContext = (user: AuthUser | null) => !!(user?.tenantOrganizationId || user?.tenantId);

export const hasOrganizationSponsoredUnlimitedAccess = (user: AuthUser | null) =>
  !!user &&
  user.isOrganizationSponsoredAccess &&
  user.effectiveAccessLevelPlan === UserAccessLevelPlanEnum.Unlimited &&
  hasOrganizationContext(user);

export const getDisplayAccessLevelPlan = (user: AuthUser | null): UserAccessLevelPlanEnum => {
  if (!user) {
    return UserAccessLevelPlanEnum.Free;
  }

  return hasOrganizationSponsoredUnlimitedAccess(user) ? user.effectiveAccessLevelPlan : user.accessLevelPlan;
};

export const repeatedCall = (callback: Function, refreshTimes = 5, timeout = 1000) => {
  setTimeout(() => {
    callback && callback();
    if (refreshTimes > 0) {
      repeatedCall(callback, refreshTimes - 1);
    }
  }, timeout);
};

export const getSupportLink = () => {
  const browser = detect();
  switch (browser?.name) {
    case 'safari':
      return 'https://support.apple.com/en-ph/guide/mac-help/mchlf6d108da/mac';
    case 'chrome':
      return 'https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop';
    case 'firefox':
      return 'https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions';
    case 'ie':
    case 'edge':
      return 'https://support.microsoft.com/en-us/windows/windows-camera-microphone-and-privacy-a83257bc-e990-d54a-d212-b5e41beba857';
    default:
      return '';
  }
};

export const getMessageTypeText = (messageType: MessageTypeEnum) => {
  switch (messageType) {
    case MessageTypeEnum.Video:
      return 'Video Message';
    case MessageTypeEnum.Audio:
      return 'Audio Message';
    default:
      return 'Written Message';
  }
};

export const ensureHttp = (url?: string) => {
  if (!url) {
    return url;
  }

  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }

  return url;
};
